import { alpha, Box, Stack, type Theme } from "@mui/material";

import { Text } from "../../components/Text";
import { ALL_SUPPORT_ARTICLES_CATEGORY } from "../constants";
import { getBackgroundStandout, getBorderBase, getMutedText, getStrongText } from "../themeHelpers";

function getInvertedText(theme: Theme) {
  return theme.palette.base?.inverted?.base ?? theme.palette.background.paper;
}

function getSectionHeadingSx(theme: Theme, isMobile: boolean) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: isMobile ? theme.typography.h6.fontSize : theme.typography.h5.fontSize,
    lineHeight: 1.2,
    color: getStrongText(theme),
  };
}

function getCategoryLabel(category: string) {
  if (category === ALL_SUPPORT_ARTICLES_CATEGORY) {
    return "All articles";
  }

  return category;
}

interface HelpCenterKnowledgeBaseProps {
  articleCountByCategory: Map<string, number>;
  displayCategories: string[];
  isMobile: boolean;
  selectedCategory: string;
  onSelectCategory: (selectedCategory: string) => void;
}

export function HelpCenterKnowledgeBase(props: HelpCenterKnowledgeBaseProps) {
  const {
    articleCountByCategory,
    displayCategories,
    isMobile,
    selectedCategory,
    onSelectCategory,
  } = props;

  if (displayCategories.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Text bold variant="body1" sx={(theme) => getSectionHeadingSx(theme, isMobile)}>
        Knowledge base
      </Text>
      <Text variant="body2" sx={(theme) => ({ color: getMutedText(theme) })}>
        Choose a category to browse articles.
      </Text>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexWrap: "wrap",
          gap: theme.spacing(2),
        })}
      >
        {displayCategories.map((category) => {
          const isSelected =
            category === selectedCategory && selectedCategory !== ALL_SUPPORT_ARTICLES_CATEGORY;

          return (
            <Box
              key={category}
              aria-label={category}
              component="button"
              type="button"
              sx={(theme) => ({
                display: "flex",
                alignItems: "flex-start",
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? "100%" : "12rem",
                maxWidth: isMobile ? undefined : "17rem",
                minHeight: isMobile ? "4.5rem" : "4rem",
                paddingTop: theme.spacing(3),
                paddingRight: theme.spacing(isMobile ? 3 : 4),
                paddingBottom: theme.spacing(3),
                paddingLeft: theme.spacing(isMobile ? 3 : 4),
                borderRadius: theme.borderRadius?.xLarge,
                border: `1px solid ${
                  isSelected
                    ? (theme.palette.base?.secondary ?? alpha(getStrongText(theme), 0.18))
                    : getBorderBase(theme)
                }`,
                backgroundColor: isSelected
                  ? (theme.palette.base?.secondary ?? getBackgroundStandout(theme))
                  : theme.palette.background.paper,
                boxShadow: isSelected
                  ? `0 0.5rem 1.25rem ${alpha(getStrongText(theme), 0.08)}`
                  : `0 1px 3px ${alpha(getStrongText(theme), 0.04)}`,
                cursor: "pointer",
                textAlign: "left",
                transition: theme.transitions.create(["border-color", "transform", "box-shadow"], {
                  duration: theme.transitions.duration.shorter,
                }),
                "&:hover": {
                  borderColor: theme.palette.base?.secondary ?? alpha(getStrongText(theme), 0.18),
                  transform: "translateY(-1px)",
                  backgroundColor: isSelected
                    ? (theme.palette.base?.secondary ?? getBackgroundStandout(theme))
                    : getBackgroundStandout(theme),
                  boxShadow: `0 0.5rem 1.25rem ${alpha(getStrongText(theme), 0.08)}`,
                },
              })}
              onClick={() => {
                onSelectCategory(
                  category === selectedCategory ? ALL_SUPPORT_ARTICLES_CATEGORY : category
                );
              }}
            >
              <Stack sx={{ minWidth: 0, gap: "0.25rem" }}>
                <Text
                  semibold
                  variant="body2"
                  sx={(theme) => ({
                    fontFamily: theme.typography.body1.fontFamily,
                    color: isSelected ? getInvertedText(theme) : theme.palette.text.primary,
                  })}
                >
                  {getCategoryLabel(category)}
                </Text>
                {(() => {
                  const count = articleCountByCategory.get(category) ?? 0;

                  return count > 0 ? (
                    <Text
                      variant="caption"
                      sx={(theme) => ({
                        color: isSelected
                          ? alpha(getInvertedText(theme), 0.72)
                          : getMutedText(theme),
                      })}
                    >
                      {count} {count === 1 ? "article" : "articles"}
                    </Text>
                  ) : null;
                })()}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
